import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ComplexArray from './complex_array';
import * as fft from './fft';
import DraggableBall from './DraggableBall';
import { toPolarCoordinates, rescale_values } from './utils';
import { saveProject, updateProject, getAudio } from './api';
import { encode } from 'wav-encoder';
import OutputLevelMeter from './OutputLevelMeter'; // OutputLevelMeterのインポート
import DownloadProgressModal from './DownloadProgressModal';
import CustomPrompt from './CustomPrompt';
import ConfirmModal from './ConfirmModal';
import LoginPromptModal from './LoginPromptModal';
import { decode as base64Decode } from 'base64-arraybuffer';
import CryptoJS from 'crypto-js';

// worklet(my-audio-processor.js)は public/ 配下のハッシュ無しURLで配信されるため
// ブラウザ/CDN にキャッシュされやすい。JSバンドル側だけ更新され worklet が古いままだと
// メッセージ仕様の不整合で3D編集・定位が動かなくなる。worklet を変更したら必ず
// このバージョンを更新してキャッシュを無効化すること。
const WORKLET_VERSION = '2026-10-15-1';
const WORKLET_URL = `/my-audio-processor.js?v=${WORKLET_VERSION}`;

// 距離→highshelf ゲイン[dB]。ゲイン変更はこの時定数[s]で滑らかに追従させる（段差によるプツ音対策）。
const GAIN_SMOOTHING = 0.03;
const distanceToGain = (r) => rescale_values(r, 50, 200, 6, -6);

const AudioProcessor = ({ user, waveSurfer, switchToFilterMode, audioContextRef, mediaSourceNodeRef, gainNodeRef, filterRef, workletNodeRef, compressorRef, blob, thru, ballCoordinates: initialBallCoordinates, indexRef: initialIndexRef, currentProject, setCurrentProject, isProjectLoaded, setIsProjectLoaded, handleDestroyConfirm, isEditing, setIsEditing, isPreviewing, setIsPreviewing }) => {
    const frameSize = 1024 * 2;
    const chunkSize = frameSize * 1;
    const hopSize = frameSize / 2; // 50% overlap
    const fftSize = 2 * frameSize - 1; // 2047
    const FC = 140; // Cut-off frequency [Hz]
    const sampleRate = 48000;
    // const defaultIndex = 10;
    // const resetIndex = 7;
    // const Ndir = 24;
    const defaultIndex = 28;
    const resetIndex = 19;
    const Ndir = 72;
    const defaultCoord = { x: -100, y: 100 };
    const resetCoord = { x: 0, y: 100 };
    const defaultDistance = Math.sqrt(defaultCoord.x ** 2 + defaultCoord.y ** 2);

    const [loading, setLoading] = useState(false);
    const fftImpulseResponses = useRef([]);
    // const audioContextRef = useRef(null);
    // const scriptNodeRef = useRef(null);
    // const filterRef = useRef(null);
    // const compressorRef = useRef(null);
    const selectedImpulseIndexRef = useRef(defaultIndex);
    const distanceRef = useRef(defaultDistance);
    const convolutionDataChangedRef = useRef(false);
    const [index, setIndex] = useState(defaultIndex);
    // const [ballCoordinates, setBallCoordinates] = useState([]);
    const ballCoordinatesRef = useRef([]);
    const isPlayingRef = useRef(false); // 再生中かどうかを示すフラグ
    const isEditingRef = useRef(false);
    const positionRef = useRef(defaultCoord); // 現在のボールの位置を保持するためのref
    const indexRef = useRef([]); // フレーム毎にindex配列を記録するためのref
    // const [isEditing, setIsEditing] = useState(false);
    // const [isPreviewing, setIsPreviewing] = useState(false);
    const isPreviewingRef = useRef(null);
    const [gain, setGain] = useState(0);
    // const gainNodeRef = useRef(null);
    const newGainNodeRef = useRef(null);
    const newFilterRef = useRef(null);
    const fImpL = useRef(null);
    const fImpR = useRef(null);
    const [countdown, setCountdown] = useState(3);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [countdownIntervalId, setCountdownIntervalId] = useState(null);
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [progress, setProgress] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const offlineContextRef = useRef(null);
    const renderCancelledRef = useRef(false); // レンダリングのキャンセルフラグ
    const [position, setPosition] = useState(defaultCoord);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    // const workletNodeRef = useRef(null);
    const offlineWorkletNodeRef = useRef(null);

    // let fImpL = new ComplexArray(fftSize);
    // let fImpR = new ComplexArray(fftSize);
    let overlapBufferL = new Float32Array(fftSize + hopSize).fill(0);
    let overlapBufferR = new Float32Array(fftSize + hopSize).fill(0);
    let extendedFrame = new Float32Array(frameSize + hopSize).fill(0);
    let prevBuffer = new Float32Array(hopSize).fill(0);

    // 再生と停止イベントリスナーを追加
    useEffect(() => {
        if (!waveSurfer) return;

        console.log('useEffect!!');
        console.log(waveSurfer);

        const handleReady = () => {
            loadFiles();
        }

        waveSurfer.on('ready', handleReady);

        const handlePlay = () => {
            isPlayingRef.current = true;
        };

        const handlePause = () => {
            isPlayingRef.current = false;
        };

        waveSurfer.on('play', handlePlay);
        waveSurfer.on('pause', handlePause);

        // Rewind to the beginning on finished playing
        waveSurfer.on('finish', () => {
            waveSurfer.setTime(0);
            waveSurfer.stop();
            setIsEditing(false);
            setIsPreviewing(false);
            workletNodeRef.current.port.postMessage({ type: 'stop' });
            isPlayingRef.current = false;
            isEditingRef.current = false;
        });

        waveSurfer.on('seeking', (currentTime) => {
            if (workletNodeRef.current) {
                workletNodeRef.current.port.postMessage({ type: 'seek', currentTime });
            }
        });

        return () => {
            waveSurfer.un('ready', handleReady);
            waveSurfer.un('play', handlePlay);
            waveSurfer.un('pause', handlePause);
        };
    }, [waveSurfer]);

    useEffect(() => {
        handleImpulseChange(index);
    }, [index]);

    useEffect(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        audioContextRef.current.audioWorklet.addModule(WORKLET_URL).then(() => {
            const workletNode = new AudioWorkletNode(audioContextRef.current, 'my-audio-processor', {
                numberOfInputs: 1,
                numberOfOutputs: 1,
                outputChannelCount: [2],
            });

            workletNodeRef.current = workletNode;
        });
    }, []);

    // Uint8ArrayをCryptoJS.lib.WordArrayに変換する関数
    const arrayBufferToWordArray = (ab) => {
        const i8a = new Uint8Array(ab);
        const a = [];
        for (let i = 0; i < i8a.length; i += 4) {
            a.push(
                (i8a[i] << 24) |
                (i8a[i + 1] << 16) |
                (i8a[i + 2] << 8) |
                (i8a[i + 3])
            );
        }
        return CryptoJS.lib.WordArray.create(a, i8a.length);
    };

    const loadAudio = async (fileName, context) => {
        try {
            const response = await getAudio(fileName);
            const encodedAudio = response.data.audio;
            const encodedKey = response.data.key;

            // Base64デコード
            const encryptedBytes = base64Decode(encodedAudio);
            const keyBytes = base64Decode(encodedKey);

            // AES復号化のためのキーとIVを設定
            const key = arrayBufferToWordArray(keyBytes);
            const iv = arrayBufferToWordArray(encryptedBytes.slice(0, 16));
            const encryptedData = arrayBufferToWordArray(encryptedBytes.slice(16));

            const decryptedData = CryptoJS.AES.decrypt(
                { ciphertext: encryptedData },
                key,
                { iv: iv, mode: CryptoJS.mode.CFB, padding: CryptoJS.pad.NoPadding }
            );

            // 復号化されたデータをUint8Arrayに変換
            const decryptedBytes = new Uint8Array(decryptedData.sigBytes);
            for (let i = 0; i < decryptedData.sigBytes; i++) {
                decryptedBytes[i] = (decryptedData.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
            }

            return await context.decodeAudioData(decryptedBytes.buffer);
        } catch (error) {
            throw error;
        }
    };

    const loadImpulseResponses = async (context) => {
        const fftBuffers = [];

        try {
            for (let i = 1; i <= Ndir + 2; i++) {
                // const fileName = `men_mid_1024_${i}.wav`;
                const fileName = `data_30_${i}.wav`;
                const buffer = await loadAudio(fileName, context);
                const impL = buffer.getChannelData(0);
                const impR = buffer.getChannelData(1);
                const paddedLength = fftSize;
                const fImpL = performFFT([...impL, ...Array(paddedLength - impL.length).fill(0)]);
                const fImpR = performFFT([...impR, ...Array(paddedLength - impR.length).fill(0)]);
                fftBuffers.push({ fImpL, fImpR });
            }
            console.log('FFT impulse responses loaded:', fftBuffers);
            fftImpulseResponses.current = fftBuffers;

            return fftBuffers;
        } catch (error) {
            console.error('Error loading impulse responses:', error);
        }
    };

    const performFFT = (input) => {
        const complexArray = new ComplexArray(input.length);
        for (let i = 0; i < input.length; i++) {
            complexArray.real[i] = input[i];
            complexArray.imag[i] = 0;
        }
        const result = fft.FFT(complexArray);
        const scaleFactor = Math.sqrt(result.length);
        for (let i = 0; i < result.length; i++) {
            result.real[i] *= scaleFactor;
            result.imag[i] *= -scaleFactor;
        }
        return result;
    };

    const performIFFT = (input) => {
        const inputClone = new ComplexArray(input.length);
        const scaleFactor = 1 / Math.sqrt(input.length);
        for (let i = 0; i < input.length; i++) {
            inputClone.real[i] = input.real[i] * scaleFactor;
            inputClone.imag[i] = -input.imag[i] * scaleFactor;
        }
        const result = fft.InvFFT(inputClone);
        return result.real;
    };

    const setConvolutionData = (fImpLData, fImpRData) => {
        fImpL.current = fImpLData;
        fImpR.current = fImpRData;
        convolutionDataChangedRef.current = true;
    };

    const initPosition = (coord, index) => {
        // 音声データのフレーム数に基づいてballCoordinatesを初期化
        const duration = waveSurfer.getDuration();
        const totalFrames = Math.ceil(duration * sampleRate / chunkSize);
        const initialCoordinates = Array(totalFrames).fill(coord);
        // setBallCoordinates(initialCoordinates);
        ballCoordinatesRef.current = initialCoordinates;
        indexRef.current = Array(totalFrames).fill(index);
        console.log('Initialized ballCoordinates with length:', totalFrames);
        console.log('duration:', duration * sampleRate);
        console.log('Initialized ballCoordinates', initialCoordinates);
        console.log('initaiindexRef:', indexRef.current);
    };

    const handlePositionResetAll = () => {
        initPosition(resetCoord, resetIndex);

        setShowResetConfirm(false);
        setPosition({ resetCoord });
        // handlePositionUpdateの代わりにイベントを発火
        const event = new CustomEvent('resetPosition', { detail: resetCoord });
        window.dispatchEvent(event);
    };

    const handlePositionReset = () => {
        setPosition({ resetCoord });
        // handlePositionUpdateの代わりにイベントを発火
        const event = new CustomEvent('resetPosition', { detail: resetCoord });
        window.dispatchEvent(event);
    };

    const loadFiles = async () => {
        console.log('loadFiles executed')
        setLoading(true);
        // const context = new (window.AudioContext || window.webkitAudioContext)();
        const context = audioContextRef.current;
        const element = waveSurfer.getMediaElement();
        // setAudioContext(context);

        try {
            console.log('loadfiles try')
            const fftBuffers = await loadImpulseResponses(context);

            if (fftBuffers.length > 0) {
                const { fImpL, fImpR } = fftBuffers[selectedImpulseIndexRef.current - 1]; // Adjust index to 0-based
                setConvolutionData(fImpL, fImpR);
            }
            const mediaNode = context.createMediaElementSource(element);
            // setSourceNode(mediaNode);

            // GainNode の作成
            const gainNode = context.createGain();
            gainNode.gain.value = 1;
            // setGainNode(gainNode);

            const biquadFilter = context.createBiquadFilter();
            biquadFilter.type = 'highshelf';
            biquadFilter.frequency.value = FC; // 調整したい周波数に設定
            biquadFilter.gain.value = gain; // 初期ゲイン値

            // 出力段のリミッター。IR(最大+14dB)・距離(+6dB)・ゲイン(+6dB)が重なると容易に 0dBFS を
            // 超えるため、ソフトニー＋やや遅いリリースで潰れ方を穏やかにする（旧: threshold 0/knee 0/
            // attack 0/release 50ms は過大入力で歪みが目立った）。Chrome は自動メイクアップゲインを
            // 足すため、通常音量でも約 +2dB 大きくなる。
            const compressor = context.createDynamicsCompressor();
            compressor.threshold.value = -6;
            compressor.knee.value = 6;
            compressor.ratio.value = 20;
            compressor.attack.value = 0.002;
            compressor.release.value = 0.25;

            // const scrNode = context.createScriptProcessor(frameSize, 1, 2);

            // audioContextRef.current = context;
            mediaSourceNodeRef.current = mediaNode;
            gainNodeRef.current = gainNode;
            // scriptNodeRef.current = scrNode;
            filterRef.current = biquadFilter;
            compressorRef.current = compressor;

            console.log('loadfiles try 2', isProjectLoaded)

            if (!isProjectLoaded) {
                initPosition(defaultCoord, defaultIndex);
            } else {
                console.log('Loaded project coordinates:', initialBallCoordinates);
                // setBallCoordinates(initialBallCoordinates);
                ballCoordinatesRef.current = initialBallCoordinates;
                indexRef.current = initialIndexRef;
                console.log('initialindexRef:', indexRef.current);
                setIsProjectLoaded(false);
            }
        } catch (error) {
            console.error('Error during loadFiles execution:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleImpulseChange = (index) => {
        selectedImpulseIndexRef.current = index;
        if (fftImpulseResponses.current.length > 0) {
            const { fImpL, fImpR } = fftImpulseResponses.current[index - 1]; // Adjust index to 0-based
            setConvolutionData(fImpL, fImpR);
        }
        // ライブ編集中の定位切替は軽量な index メッセージで worklet に伝え、
        // インパルス切替はフレーム境界で worklet 側が行う（毎フレームの大容量転送を避ける）。
        if (workletNodeRef.current) {
            workletNodeRef.current.port.postMessage({ type: 'liveIndex', index });
        }
    };

    const handleDistanceChange = (r) => {
        distanceRef.current = r;
        const newGain = distanceToGain(r);
        setGain(newGain);
        // .value への即時代入は段差になりプツ音が出るため、時定数で滑らかに追従させる
        if (filterRef.current) {
            filterRef.current.gain.setTargetAtTime(newGain, audioContextRef.current.currentTime, GAIN_SMOOTHING);
        }
    }

    const handleGainChange = (event) => {
        const gainValue = event.target.value;
        if (gainNodeRef.current) {
            gainNodeRef.current.gain.value = gainValue;
        }
        if (newGainNodeRef.current) {
            newGainNodeRef.current.gain.value = gainValue;
        }
    };

    const handleStartEditing = async () => {
        const handleAudioProcess = () => {
            if (!isEditingRef.current) return; // 編集中でない場合は処理しない

            const currentTime = waveSurfer.getCurrentTime();
            const chunkIndex = Math.round(currentTime * sampleRate / chunkSize);

            // 編集区間の位置とインデックスを記録する。
            ballCoordinatesRef.current[chunkIndex] = { ...positionRef.current };
            indexRef.current[chunkIndex] = selectedImpulseIndexRef.current;

            // 定位切替は liveIndex（handleImpulseChange）経由で worklet がフレーム境界で行う。
            // ここで毎フレームインパルスバッファを postMessage しない（大容量転送によるジャンク/ノイズを防ぐ）。
        };
    
        if (waveSurfer.isPlaying()) {
            waveSurfer.stop();
            workletNodeRef.current.port.postMessage({ type: 'stop' });
            waveSurfer.un('audioprocess', handleAudioProcess);
            isPlayingRef.current = false;
            isEditingRef.current = false;
            setIsEditing(false);
            setIsCountingDown(false);
            if (countdownIntervalId) {
                clearInterval(countdownIntervalId);
            }
            return;
        }
    
        setIsEditing(true);
        isEditingRef.current = true;
        isPlayingRef.current = true;
        switchToFilterMode(gainNodeRef.current, filterRef.current, workletNodeRef.current, compressorRef.current);
    
        if (isCountingDown) {
            // カウントダウン中にボタンが押されたらカウントダウンを停止
            setIsEditing(false);
            if (countdownIntervalId) {
                clearInterval(countdownIntervalId);
            }
            setIsCountingDown(false);
            setCountdown(0);
            return;
        }
    
        // カウントダウンを開始
        setIsCountingDown(true);
        setCountdown(3);
    
        const intervalId = setInterval(() => {
            setCountdown(prevCountdown => {
                if (prevCountdown <= 1) {
                    clearInterval(intervalId);
                    setIsCountingDown(false);
    
                    (async () => {
                        // ここでデータを送信し、Promiseを待機します
                        await new Promise((resolve) => {
                            workletNodeRef.current.port.onmessage = (event) => {
                                if (event.data.type === 'setImpulseResponsesComplete') {
                                    resolve();
                                }
                            };
    
                            console.log('sending setConvolutionData');
                            workletNodeRef.current.port.postMessage({
                                type: 'setfImp',
                                fImpL: {
                                    real: fImpL.current.real.buffer,
                                    imag: fImpL.current.imag.buffer
                                },
                                fImpR: {
                                    real: fImpR.current.real.buffer,
                                    imag: fImpR.current.imag.buffer
                                }
                            });
    
                            // 全インパルス応答も同梱し、ライブ編集の定位切替を worklet 内部で
                            // フレーム境界に同期させる（indexes は送らないため内部 indexes 選択は無効）。
                            workletNodeRef.current.port.postMessage({
                                type: 'setImpulseResponses',
                                fftImpulseResponses: fftImpulseResponses.current.map(response => ({
                                    fImpL: {
                                        real: response.fImpL.real.buffer,
                                        imag: response.fImpL.imag.buffer
                                    },
                                    fImpR: {
                                        real: response.fImpR.real.buffer,
                                        imag: response.fImpR.imag.buffer
                                    }
                                }))
                            });
                        });

                        await new Promise(resolve => setTimeout(resolve, 100));
                        // 開始時点で選択中の方向を worklet に伝える（最初のフレームから正しい定位に）。
                        workletNodeRef.current.port.postMessage({ type: 'liveIndex', index: selectedImpulseIndexRef.current });
                        workletNodeRef.current.port.postMessage({ type: 'start' });
                        waveSurfer.play();
                        waveSurfer.on('audioprocess', handleAudioProcess);
                    })();
                }
                return prevCountdown - 1;
            });
        }, 1000);
    
        setCountdownIntervalId(intervalId);
    };    

    const handlePlayPreview = async () => {
        const handleAudioProcess = () => {
            if (!isPlayingRef.current) return; // 再生中でない場合は処理しない

            const currentTime = waveSurfer.getCurrentTime();
            const chunkIndex = Math.round(currentTime * sampleRate / chunkSize);

            if (chunkIndex >= ballCoordinatesRef.current.length) return;

            // 音声定位（インパルス応答の選択）は worklet 内部でフレーム毎に確定するため、
            // ここでの 'update' postMessage は行わない（main→worklet の時計ずれによる定位飛びを回避）。
            // ここでは距離ゲイン（filter）の反映のみ行う。
            handleDistanceChange(toPolarCoordinates(ballCoordinatesRef.current[chunkIndex].x, ballCoordinatesRef.current[chunkIndex].y).r);
        };

        if (waveSurfer.isPlaying()) {
            waveSurfer.stop();
            isPlayingRef.current = false;
            isEditingRef.current = false;
            setIsPreviewing(false);
            workletNodeRef.current.port.postMessage({ type: 'stop' });
            waveSurfer.un('audioprocess', handleAudioProcess);
            return;
        }

        setIsPreviewing(true);
        isPreviewingRef.current = true;
        isPlayingRef.current = true;
        switchToFilterMode(gainNodeRef.current, filterRef.current, workletNodeRef.current, compressorRef.current);

        // ここでデータを送信し、Promiseを待機します
        await new Promise((resolve) => {
            workletNodeRef.current.port.onmessage = (event) => {
                if (event.data.type === 'setImpulseResponsesComplete') {
                    resolve();
                }
            };

            console.log('sending setConvolutionData');
            workletNodeRef.current.port.postMessage({
                type: 'setfImp',
                fImpL: {
                    real: fImpL.current.real.buffer,
                    imag: fImpL.current.imag.buffer
                },
                fImpR: {
                    real: fImpR.current.real.buffer,
                    imag: fImpR.current.imag.buffer
                }
            });

            // indexes と全インパルス応答を同梱し、worklet 内部でフレーム毎に定位を選択させる
            // （オフライン/ダウンロード経路と同じ方式。定位が音声とフレーム精度で一致する）。
            workletNodeRef.current.port.postMessage({
                type: 'setImpulseResponses',
                indexes: new Uint32Array(indexRef.current).buffer,
                fftImpulseResponses: fftImpulseResponses.current.map(response => ({
                    fImpL: {
                        real: response.fImpL.real.buffer,
                        imag: response.fImpL.imag.buffer
                    },
                    fImpR: {
                        real: response.fImpR.real.buffer,
                        imag: response.fImpR.imag.buffer
                    }
                }))
            });
        });

        await new Promise(resolve => setTimeout(resolve, 100));
        workletNodeRef.current.port.postMessage({ type: 'start' });
        waveSurfer.play();
        waveSurfer.on('audioprocess', handleAudioProcess);

        let chunkIndex = 0;
        const interval = setInterval(() => {
            if (!isPlayingRef.current) {
                clearInterval(interval);
                return;
            }

            const currentTime = waveSurfer.getCurrentTime();
            chunkIndex = Math.round(currentTime * sampleRate / chunkSize);
            if (chunkIndex < ballCoordinatesRef.current.length) {
                positionRef.current = ballCoordinatesRef.current[chunkIndex];
                const event = new CustomEvent('updatePosition', { detail: positionRef.current });
                window.dispatchEvent(event);
                console.log('Updating ball position to:', positionRef.current, 'at chunkIndex:', chunkIndex);
            } else {
                clearInterval(interval);
            }
        }, 1000 / 48);
    };

    const handleSaveProject = async (projectName) => {
        console.log('wave:', waveSurfer, 'user:', user, 'blob:', blob);
        if (waveSurfer && user) {
            const projectData = {
                name: projectName,
                audio_data: blob,
                position_ref: ballCoordinatesRef.current,
                index_ref: indexRef.current
            };
            setIsLoading(true);  // スピナー表示のためのローディング状態をセット
            try {
                const response = await saveProject(projectData);
                setCurrentProject(response.data);  // 現在のプロジェクトをセット
                setMessage('プロジェクトが保存されました');
                setIsPromptOpen(true); // メッセージ表示用にプロンプトを再度開く
            } catch (error) {
                console.error('Error saving project', error);
                if (error.response) {
                    console.error('Server responded with status code:', error.response.status);
                    console.error('Response data:', error.response.data);
                }
            } finally {
                setIsLoading(false);  // ローディング状態を解除
            }
        }
    };

    const handleUpdateProject = async () => {
        console.log('project', currentProject, currentProject.id);

        if (waveSurfer && currentProject) {
            const projectData = {
                name: currentProject.name,
                audio_data: blob,
                position_ref: ballCoordinatesRef.current,
                index_ref: indexRef.current
            };
            try {
                await updateProject(currentProject.id, projectData);
                setMessage('プロジェクトが更新されました');
                setIsPromptOpen(true); // メッセージ表示用にプロンプトを再度開く
            } catch (error) {
                console.error('Error updating project', error);
            }
        }
    };

    const handleDownload = async () => {
        if (!blob) return;
        const handleAudioProcess = () => {
            if (renderCancelledRef.current) return; // キャンセルフラグが立っている場合は処理しない

            const currentTime = offlineContextRef.current.currentTime;

            // 進捗度を更新
            const progress = Math.min(100, (currentTime * sampleRate / totalFrames) * 100);
            setProgress(progress);
        };

        setShowModal(true); // モーダルを表示
        renderCancelledRef.current = false; // キャンセルフラグをリセット

        // BlobをAudioBufferにデコード
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

        const totalFrames = audioBuffer.length;
        offlineContextRef.current = new OfflineAudioContext(2, totalFrames, sampleRate);

        // 新しいフィルタノードとコンプレッサーノードを作成して設定をコピー
        const newGainNode = offlineContextRef.current.createGain();
        const newFilter = offlineContextRef.current.createBiquadFilter();
        const newCompressor = offlineContextRef.current.createDynamicsCompressor();
        copyGainSettings(gainNodeRef.current, newGainNode);
        copyFilterSettings(filterRef.current, newFilter);
        copyCompressorSettings(compressorRef.current, newCompressor);
        newGainNodeRef.current = newGainNode;
        newFilterRef.current = newFilter;

        // 距離ゲインはレンダリング前に全区間ぶん予約する。レンダリング中に setInterval で
        // 書き換えると段差でプツ音が出るうえ、反映タイミングが不定で毎回出力が変わるため。
        // チャンク k はプレビューと同じく round(t / chunkDuration) === k の区間に対応させる。
        const chunkDuration = chunkSize / sampleRate;
        let prevGain = null;
        ballCoordinatesRef.current.forEach((coord, k) => {
            if (!coord) return;
            const g = distanceToGain(toPolarCoordinates(coord.x, coord.y).r);
            if (g === prevGain) return;
            if (prevGain === null) {
                newFilter.gain.setValueAtTime(g, 0);
            } else {
                newFilter.gain.setTargetAtTime(g, Math.max(0, (k - 0.5) * chunkDuration), GAIN_SMOOTHING);
            }
            prevGain = g;
        });

        const inputBuffer = offlineContextRef.current.createBufferSource();
        inputBuffer.buffer = audioBuffer;

        await offlineContextRef.current.audioWorklet.addModule(WORKLET_URL); // 追加（キャッシュバスティング付き）

        const workletNode = new AudioWorkletNode(offlineContextRef.current, 'my-audio-processor', {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            outputChannelCount: [2],
        });
        offlineWorkletNodeRef.current = workletNode;

        inputBuffer.connect(offlineWorkletNodeRef.current);
        offlineWorkletNodeRef.current.connect(newGainNodeRef.current);
        newGainNodeRef.current.connect(newFilterRef.current);
        newFilterRef.current.connect(newCompressor);
        newCompressor.connect(offlineContextRef.current.destination);
        inputBuffer.start();

        console.log('sending start')
        offlineWorkletNodeRef.current.port.postMessage({ type: 'offline' });

        console.log('sending setConvolutionData');
        offlineWorkletNodeRef.current.port.postMessage({
            type: 'setfImp',
            fImpL: {
                real: fImpL.current.real.buffer,
                imag: fImpL.current.imag.buffer
            },
            fImpR: {
                real: fImpR.current.real.buffer,
                imag: fImpR.current.imag.buffer
            }
        });

        offlineWorkletNodeRef.current.port.postMessage({
            type: 'setImpulseResponses',
            indexes: new Uint32Array(indexRef.current).buffer,
            fftImpulseResponses: fftImpulseResponses.current.map(response => ({
                fImpL: {
                    real: response.fImpL.real.buffer,
                    imag: response.fImpL.imag.buffer
                },
                fImpR: {
                    real: response.fImpR.real.buffer,
                    imag: response.fImpR.imag.buffer
                }
            }))
        });

        // 一定間隔で handleAudioProcess を呼び出す
        const intervalId = setInterval(() => {
            handleAudioProcess();

            // 全てのフレームが処理されたらクリア
            if (offlineContextRef.current.currentTime * sampleRate >= totalFrames) {
                clearInterval(intervalId);
            }
        }, 1000 / 1000); // 60 FPS 相当の間隔で呼び出し

        offlineContextRef.current.startRendering().then(async (renderedBuffer) => {
            clearInterval(intervalId); // レンダリングが完了したらクリア
            workletNodeRef.current.port.postMessage({ type: 'stop' });

            if (renderCancelledRef.current) {
                return; // キャンセルフラグが立っている場合は処理を中断
            }

            const outputBufferL = renderedBuffer.getChannelData(0);
            const outputBufferR = renderedBuffer.getChannelData(1);

            const wavBuffer = await encode({
                sampleRate: offlineContextRef.current.sampleRate,
                channelData: [outputBufferL, outputBufferR],
                bitDepth: 24,
            });

            const blob2 = new Blob([wavBuffer], { type: 'audio/wav' });
            const url = URL.createObjectURL(blob2);

            const link = document.createElement('a');
            link.href = url;
            link.download = currentProject?.name ? `${currentProject.name}.wav` : 'imavo_download.wav';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setProgress(100); // 進捗度を100%に設定
            setShowModal(false); // モーダルを非表示
            setProgress(0); // 進捗度をリセット
        });
    };

    const handleDownloadCancel = () => {
        renderCancelledRef.current = true; // キャンセルフラグを立てる
        setShowModal(false); // モーダルを非表示
        setProgress(0); // 進捗度をリセット

    };

    const handleEditReset = () => {
        setShowResetConfirm(true);
    };

    const openPrompt = () => {
        if (!user) {
            // ゲストモードの場合はログインを促すモーダルを表示
            setShowLoginPrompt(true);
            return;
        }
        setMessage(''); // メッセージをリセット
        setIsPromptOpen(true);
    };

    const closePrompt = () => {
        setIsPromptOpen(false);
    };

    const handleUpdateProjectClick = () => {
        if (!user) {
            // ゲストモードの場合はログインを促すモーダルを表示
            setShowLoginPrompt(true);
            return;
        }
        handleUpdateProject();
    };

    return (
        <>
            <img src="tips1.svg" alt="Tips" className="tips-image" />
            {/* <hr className="editor-divider" /> */}
            <div className="editor-container">
                {ReactDOM.createPortal(
                    <div className="save-buttons">
                        <button onClick={openPrompt} disabled={thru || isEditing || isPreviewing || loading} className="editor-button new-save">
                            名前を付けて保存
                        </button>
                        <button onClick={handleUpdateProjectClick} disabled={thru || (!user || !currentProject) || isEditing || isPreviewing || loading} className={`editor-button ${currentProject && user ? 'overwrite-save' : 'bg-gray'}`}>
                            上書き保存
                        </button>
                    </div>,
                    document.getElementById('save-buttons')
                )}
                <CustomPrompt
                    isOpen={isPromptOpen}
                    onClose={closePrompt}
                    onSave={handleSaveProject}
                    message={message}
                    isLoading={isLoading}
                />
                <div className="content-container">
                    <div className="button-container">
                        <img src="tips2.svg" alt="Tips" className="tips-image" />
                        <button
                            onClick={handleStartEditing}
                            disabled={thru || isPreviewing || loading || ballCoordinatesRef.current.length === 0}
                            className={`editor-button ${isEditing ? "blinking" : ""}`}
                        >
                            {loading ? (
                                <div className="loading-spinner"></div>
                            ) : (
                                isEditing ? (countdown > 0 ? `${countdown}（クリックで停止）` : "停止") : "3D編集をはじめる"
                            )}
                            <div className="small-text">編集した区間のみ上書きされます</div>
                        </button>
                        <button onClick={handlePlayPreview} disabled={thru || isEditing || loading || ballCoordinatesRef.current.length === 0} className="editor-button">
                            {isPreviewing ? "停止" : "プレビュー再生"}
                        </button>
                        <div className="editor-buttons-row">
                            <button onClick={handleEditReset} disabled={thru || isEditing || isPreviewing} className="editor-button edit-reset-button">3D編集リセット</button>
                            <button onClick={handleDestroyConfirm} disabled={thru || isEditing || isPreviewing} className="editor-button destroy-button">破棄する</button>
                        </div>
                        <button onClick={handleDownload} disabled={thru || isEditing || isPreviewing} className="editor-button download-button">ダウンロード</button>
                        <DownloadProgressModal show={showModal} progress={progress} onCancel={handleDownloadCancel} />
                        {showResetConfirm && (
                            <ConfirmModal
                                message="3D編集をリセットしますか？"
                                onConfirm={handlePositionResetAll}
                                onCancel={() => setShowResetConfirm(false)}
                            />
                        )}
                        {showLoginPrompt && (
                            <LoginPromptModal
                                onClose={() => setShowLoginPrompt(false)}
                            />
                        )}
                    </div>
                    <div className="draggable-container">
                        <DraggableBall
                            onIndexChange={handleImpulseChange}
                            onDistanceChange={handleDistanceChange}
                            thru={thru}
                            setPositionRef={positionRef}
                            disabled={isPreviewing}
                            position={position}
                            setPosition={setPosition}
                            Ndir={Ndir}
                        />
                        <div className="controls-and-buttons">
                            <button onClick={handlePositionReset} className="reset-button" disabled={thru || isPreviewing}>ポジションリセット</button>
                            <div className="control-container">
                                <div className="control-item">
                                    <label className="control-label">ゲイン調整</label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="2"
                                        step="0.01"
                                        defaultValue="1"
                                        onChange={handleGainChange}
                                        className="vertical-slider"
                                    />
                                </div>
                                <div className="control-item">
                                    <label className="control-label">出力レベル</label>
                                    {compressorRef.current && <OutputLevelMeter audioNode={compressorRef.current} isEditing={isEditing} isPreviewing={isPreviewing} />}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );

}

const copyGainSettings = (sourceGain, targetGain) => {
    targetGain.gain.value = sourceGain.gain.value;
};

const copyFilterSettings = (sourceFilter, targetFilter) => {
    targetFilter.type = sourceFilter.type;
    targetFilter.frequency.value = sourceFilter.frequency.value;
    targetFilter.Q.value = sourceFilter.Q.value;
    targetFilter.gain.value = sourceFilter.gain.value;
};

const copyCompressorSettings = (sourceCompressor, targetCompressor) => {
    targetCompressor.threshold.value = sourceCompressor.threshold.value;
    targetCompressor.knee.value = sourceCompressor.knee.value;
    targetCompressor.ratio.value = sourceCompressor.ratio.value;
    targetCompressor.attack.value = sourceCompressor.attack.value;
    targetCompressor.release.value = sourceCompressor.release.value;
};


export default AudioProcessor;
